'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

import Image from 'next/image';
import Link from 'next/link';
import { ChevronDown, MapPin, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { useQuery } from '@tanstack/react-query';

import { ImageCarousel } from '../../../_components/image-carousel';
import { LocationCardShell } from '../../../_components/location-card-shell';

const PLACEHOLDER_IMAGE = '/landing-pages/delta/images/location-placeholder.png';

/** How many cards to show before "load more" (keeps the section a teaser). */
const PREVIEW_LIMIT = 8;

/**
 * How many additional cards each "load more" click reveals. Deliberately NOT
 * "show everything": this tenant already has ~90 public clubs and expects 100+,
 * and every card mounts a next/image. Rendering the whole roster in one click
 * would fire ~100 image requests and produce a section several screens tall.
 * Batching keeps each interaction cheap while still making every club reachable.
 */
const LOAD_MORE_STEP = 12;

/**
 * Deterministic FNV-1a hash of a string → unsigned 32-bit int. Used to give the
 * unfiltered teaser a shuffled-looking but STABLE order: seeding the sort from
 * each location's id means the server render and client hydration produce the
 * same sequence (unlike Math.random(), which would mismatch and break
 * hydration). Pure — same input always yields the same output.
 */
function seededKey(id: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    hash ^= id.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

export interface PublicLocation {
  id: string;
  slug: string;
  name: string;
  name_alt: string | null;
  city: string | null;
  city_alt: string | null;
  gender: 'male' | 'female' | 'both';
  image_urls: string[] | null;
  region_id: string | null;
  region_name: string | null;
  region_name_alt: string | null;
  neighborhood: string | null;
  neighborhood_alt: string | null;
}

/** Resolve a bilingual pair, preferring the Arabic (_alt) value in RTL and
 *  falling back to the primary when the Arabic is missing (and vice-versa). */
function pickLabel(
  primary: string | null,
  alt: string | null,
  preferAlt: boolean,
): string {
  if (preferAlt) return (alt && alt.trim()) || (primary ?? '');
  return (primary ?? '') || (alt ?? '');
}

/**
 * "Clubs Locations" showcase beneath the Delta hero. Mirrors the client's
 * neighbourhood-clubs directory: a search + region/city filter bar over a grid
 * of location preview cards (image or the brand-mark placeholder when a venue
 * has no photo). Reads the same get_public_locations RPC the booking funnel
 * uses, so it always reflects active, public venues. Each card deep-links into
 * /book?location=<slug>.
 */
export function ClubsLocationsSection({
  accountId,
  initialLocations,
}: {
  accountId?: string;
  initialLocations?: PublicLocation[];
}) {
  const supabase = useSupabase();
  const { t, i18n } = useTranslation('landing');
  const preferAlt = i18n.language === 'ar';

  // Server prefetches locations and passes them as `initialData`, so the
  // section renders fully on first paint instead of flashing blank while this
  // query hydrates. The query still runs in the background to keep the list
  // fresh, but the initial render never waits on it.
  const { data: locations = [] } = useQuery({
    queryKey: ['delta-clubs-locations', accountId],
    queryFn: async () => {
      if (!accountId) return [] as PublicLocation[];
      const { data, error } = await supabase.rpc('get_public_locations', {
        p_account_id: accountId,
      });
      if (error || !data) return [] as PublicLocation[];
      return data as unknown as PublicLocation[];
    },
    enabled: !!accountId,
    initialData: initialLocations,
  });

  const ref = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Filters: search + region + city. Same spirit as the booking funnel /
  // admin Locations filters, scaled down for a public teaser.
  const [search, setSearch] = useState('');
  const [regionId, setRegionId] = useState('all');
  const [city, setCity] = useState('all');

  // The section opens as a teaser (PREVIEW_LIMIT cards) and grows in place a
  // batch at a time, so every club is reachable without leaving the landing
  // page and without mounting ~100 cards at once.
  const [visibleCount, setVisibleCount] = useState(PREVIEW_LIMIT);

  // Region/city filter VALUES stay the primary (region_id / primary city) so
  // filtering logic is locale-independent; only the displayed LABEL localizes.
  const regionOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const l of locations) {
      if (l.region_id && (l.region_name || l.region_name_alt)) {
        map.set(
          l.region_id,
          pickLabel(l.region_name, l.region_name_alt, preferAlt),
        );
      }
    }
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [locations, preferAlt]);

  // City options SUB-FILTER by the selected region (same as the booking
  // funnel): once a region is chosen, only its cities are offered. When no
  // region is selected, every city is listed.
  const cityOptions = useMemo(() => {
    // Keyed by the primary city (the filter value); label localized.
    const map = new Map<string, string>();
    for (const l of locations) {
      if (regionId !== 'all' && l.region_id !== regionId) continue;
      const key = l.city?.trim();
      if (key) map.set(key, pickLabel(l.city, l.city_alt, preferAlt));
    }
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [locations, regionId, preferAlt]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return locations.filter((l) => {
      if (regionId !== 'all' && l.region_id !== regionId) return false;
      if (city !== 'all' && l.city !== city) return false;
      if (q) {
        const hay = [l.name, l.name_alt, l.city, l.neighborhood, l.neighborhood_alt]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [locations, search, regionId, city]);

  const hasFilters = search.trim() !== '' || regionId !== 'all' || city !== 'all';

  // Changing any filter restarts paging at the teaser size — otherwise a
  // visitor who expanded to 60 cards would see a new, narrower search render 60
  // rows deep with no way back to a short list.
  useEffect(() => {
    setVisibleCount(PREVIEW_LIMIT);
  }, [search, regionId, city]);

  // When unfiltered, show a sampled subset so the teaser surfaces a spread of
  // venues rather than always the first N in DB order. The order is a stable,
  // id-seeded permutation — NOT Math.random(): the section is SSR'd as a
  // client component, so a random order would differ between the server render
  // and client hydration and trigger a hydration mismatch. Seeding from the
  // location ids makes server and client agree while still looking shuffled.
  // When the user filters, show the full filtered result instead.
  const sample = useMemo(() => {
    return [...filtered].sort((a, b) => seededKey(a.id) - seededKey(b.id));
    // Only re-sample when the underlying locations change, not on every filter
    // keystroke (filtered branch handles the filtered view separately below).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations]);

  // Both the filtered and unfiltered views page through LOAD_MORE_STEP batches:
  // a broad search (e.g. a single letter) can match nearly the whole roster, so
  // the filtered branch needs the same cap as the teaser.
  const source = hasFilters ? filtered : sample;
  const preview = source.slice(0, visibleCount);
  const remaining = source.length - preview.length;

  if (locations.length === 0) return null;

  return (
    <section
      ref={ref}
      id="clubs-locations"
      className="bg-white px-4 py-20 sm:px-6 lg:px-12 lg:py-28"
    >
      <div className="mx-auto max-w-[1400px]">
        {/* Header */}
        <div
          className={`mb-10 text-center transition-all duration-700 ${
            inView ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
          }`}
        >
          <h2 className="text-3xl font-extrabold tracking-tight text-[var(--delta-ink)] sm:text-4xl lg:text-5xl">
            {t('deltaClubs.titleLead')}{' '}
            <span className="text-[var(--delta-green)]">
              {t('deltaClubs.titleHighlight')}
            </span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-neutral-500 sm:text-lg">
            {t('deltaClubs.subtitle')}
          </p>
        </div>

        {/* Filter bar */}
        <div
          className={`mx-auto mb-10 flex max-w-4xl flex-col gap-3 rounded-2xl border border-neutral-200 bg-neutral-50 p-3 transition-all duration-700 sm:flex-row sm:items-center ${
            inView ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
          }`}
        >
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('deltaClubs.searchPlaceholder')}
              className="h-11 w-full rounded-full border border-neutral-200 bg-white ps-9 pe-4 text-sm text-neutral-800 outline-none focus:border-[var(--delta-green)]"
            />
          </div>

          {regionOptions.length > 0 && (
            <div className="relative sm:w-44">
              <select
                value={regionId}
                onChange={(e) => {
                  // Changing the region resets the city: the previously chosen
                  // city may not belong to the new region, which would filter
                  // the grid down to nothing and leave the city select showing
                  // a value no longer in its (now region-scoped) options.
                  setRegionId(e.target.value);
                  setCity('all');
                }}
                className="h-11 w-full appearance-none rounded-full border border-neutral-200 bg-white ps-4 pe-9 text-sm text-neutral-800 outline-none focus:border-[var(--delta-green)]"
              >
                <option value="all">{t('deltaClubs.allRegions')}</option>
                {regionOptions.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            </div>
          )}

          {cityOptions.length > 0 && (
            <div className="relative sm:w-40">
              <select
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="h-11 w-full appearance-none rounded-full border border-neutral-200 bg-white ps-4 pe-9 text-sm text-neutral-800 outline-none focus:border-[var(--delta-green)]"
              >
                <option value="all">{t('deltaClubs.allCities')}</option>
                {cityOptions.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            </div>
          )}
        </div>

        {/* Grid */}
        {preview.length === 0 ? (
          <p className="text-center text-neutral-400">
            {t('deltaClubs.noMatch')}
          </p>
        ) : (
          <div
            id="clubs-locations-grid"
            className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4"
          >
            {preview.map((location, idx) => (
              <LocationCard
                key={location.id}
                location={location}
                inView={inView}
                delay={Math.min(idx, 7) * 80}
                preferAlt={preferAlt}
                genderMale={t('deltaClubs.genderMale')}
                genderFemale={t('deltaClubs.genderFemale')}
              />
            ))}
          </div>
        )}

        {/* Reveal the roster a batch at a time, plus a count so the visitor
            always knows how many clubs are still below the fold. */}
        {remaining > 0 && (
          <div className="mt-10 flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={() =>
                setVisibleCount((count) => count + LOAD_MORE_STEP)
              }
              aria-controls="clubs-locations-grid"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-[var(--delta-green)] px-8 text-sm font-semibold text-white transition-colors hover:bg-[var(--delta-green-dark)]"
            >
              {t('deltaClubs.loadMore')}
              <ChevronDown className="h-4 w-4" />
            </button>
            <p className="text-sm text-neutral-500">
              {t('deltaClubs.showingCount', {
                shown: preview.length,
                total: source.length,
              })}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

function LocationCard({
  location,
  inView,
  delay,
  preferAlt,
  genderMale,
  genderFemale,
}: {
  location: PublicLocation;
  inView: boolean;
  delay: number;
  preferAlt: boolean;
  genderMale: string;
  genderFemale: string;
}) {
  const images = location.image_urls ?? [];
  const hasImages = images.length > 0;
  const href = `/book?location=${location.slug}`;
  const gender =
    location.gender === 'male'
      ? genderMale
      : location.gender === 'female'
        ? genderFemale
        : null;
  const name = pickLabel(location.name, location.name_alt, preferAlt);
  const title = gender ? `${name} - ${gender}` : name;
  // Show BOTH the neighborhood and the city (localized) so cards carry full
  // geographic context — "Neighborhood, City". Either part may be missing, so
  // build from the present pieces and join with a comma only when both exist.
  const neighborhood = pickLabel(
    location.neighborhood,
    location.neighborhood_alt,
    preferAlt,
  );
  const cityLabel = pickLabel(location.city, location.city_alt, preferAlt);
  // Arabic comma in RTL, Latin comma otherwise.
  const separator = preferAlt ? '، ' : ', ';
  const place = Array.from(
    new Set([neighborhood, cityLabel].filter(Boolean)),
  ).join(separator);

  return (
    // The shell carries the click (see its docblock for why the card can't just
    // be one big <a>); the title below stays a real anchor for keyboard, SEO
    // and open-in-new-tab. Sub-threshold taps on the photo bubble up to the
    // shell; a swipe is swallowed by the carousel.
    <LocationCardShell
      href={href}
      className={`flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm transition-all duration-700 hover:-translate-y-1 hover:shadow-md ${
        inView ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {/* Gallery / placeholder. Arrows, dots and swipe replace the old
          hover-zoom affordance. */}
      {hasImages ? (
        // Up to ~100 cards in this grid: no preload hints, and keep the Next
        // image optimizer this card used before it became a gallery.
        <ImageCarousel
          images={images}
          alt={name}
          className="bg-[var(--delta-ink)]"
          // Must mirror the grid (1 / 2 / 4 columns). This is the one call site
          // that keeps the Next optimizer, so `sizes` actually picks the srcSet
          // candidate here — the carousel's default would ship a 2x-upscaled
          // image on phones.
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
          priority={false}
          unoptimized={false}
        />
      ) : (
        <div className="relative aspect-[4/3] overflow-hidden bg-[var(--delta-ink)]">
          <Image
            src={PLACEHOLDER_IMAGE}
            alt={name}
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
            className="object-contain p-8 opacity-40"
          />
        </div>
      )}

      {/* Info — place row pinned to the bottom so it lines up across cards
          regardless of whether the name wraps to one or two lines. */}
      <div className="flex flex-1 flex-col gap-2 p-4">
        <h3 className="line-clamp-2 font-bold leading-snug text-[var(--delta-ink)]">
          {/* No stopPropagation needed: LocationCardShell ignores clicks that
              start on a nested control, which is the only reliable place to
              handle it — a caller's onClick can't stop a middle-click. */}
          <Link href={href}>{title}</Link>
        </h3>
        {place && (
          <div className="mt-auto flex items-center gap-1.5 text-sm text-neutral-500">
            <MapPin className="h-4 w-4 shrink-0 text-[var(--delta-green)]" />
            <span className="truncate">{place}</span>
          </div>
        )}
      </div>
    </LocationCardShell>
  );
}
